import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom'
import { Loading } from '../../components/Loading';
import { MainButton } from '../../components/MainButton'
import { ConfigType } from '../../contexts/AppConfigContext';
import { useAppConfig } from '../../hooks/useAppConfig';
import { usePluginspace } from '../../hooks/usePluginspace';
import './index.css'
import Swal from 'sweetalert2';
import api from '../../services/api';
import InputMask from "react-input-mask";
import { Checkbox } from '../../components/Checkbox';
import { cpfCnpjNoMask, datetimeToDateBR, telephoneNoMask } from '../../functions/mask';
import { getWeekDay } from '../../functions/util';
import { centsToFloat, floatToPrice } from '../../functions/price_functions';

type ImageType = {
    id: string;
    url: string;
}

type HourType = {
    id: string;
    hour: string;
}

type DayType = {
    able: boolean;
    day: 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday'
    hours: HourType[];
}

type EnvironmentType = {
    id: string;
    name: string;
    seatsAvailable: number;
    tables: number;
}

type BookingConfigType = {
    id: string;
    able: boolean;
    dayConfirm: boolean;
    expiryTime: number;
    maxPeople: number;
    paymentConfirm: boolean;
    valuePaymentConfirm: number;
    days: DayType[];
    images: ImageType[];
    environment: EnvironmentType[];
}

type BookingToSendType = {
    date: string;
    bookingHourId: number;
    bookingEnvironmentId: number;
    email: string;
    bookingConfigId: number;
    people: number;
    name: string;
    phone: string;
    hour: string;
}

type PlansType = {
    id: number;
    codePlan: number;
    installment: number;
}

type IntegrationType = {
    id: number;
    pluginconfigId: number;
    brand: string;
    cardOperator: number;
    codePay: string;
    nameGateway: string;
    paymentPlans: PlansType[];
    typePay: string;
}

type CardTotvsType = {
	amount: number,
	deliveryfee: number,
	installments: number | string,
	cardholderName: string,
	cardNumber: null,
	nameCardBrand: string
	expirationMonth: string,
	expirationYear: string,
	securityCode: string,
	brand: string,
	tokenizedPan: string,
    gatewayId?: number;
    plugintemplateId?: number;
    email?: string;
    cpf?: string;
    phone?: string;
    fingerprintSessionId?: string;
}

export function BookingPayment() {
    const { pluginspace_id } = useParams();
    const { registerAccess, getToken, pluginspaceData } = usePluginspace()
    const navigate = useNavigate();

    const { getAppConfig } = useAppConfig();
    const [plugintemplateId, setPlugintemplateId] = useState(0);
    const [config, setConfig] = useState<ConfigType | void>();
    const [loading, setLoading] = useState(true);
    const [bookingConfig, setBookingConfig] = useState<BookingConfigType | null>(null);
    const [booking, setBooking] = useState<BookingToSendType | null>(null);
    const [name, setName] = useState('');
    const [number, setNumber] = useState('');
    const [cvv, setCvv] = useState('');
    const [cpf, setCpf] = useState('');
    const [brand, setBrand] = useState('');
    const [integrationBrands, setIntegrationBrands] = useState<IntegrationType[]>([])
    const [cardDate, setCardDate] = useState('');
    const [maskCard, setMaskCard] = useState('9999 9999 9999 99')
    const [maskCvv, setMaskCvv] = useState('999')

    useEffect(() => {
        registerAccess('booking/payment')
        init();
    }, []);

    async function init() {
        setLoading(true);
        await getConfig()
        await getBookingConfig();
        getBooking();
        setLoading(false)
    }

    async function loadBrands(gatewayId: number){
        try {
            
            const resPluginspace = await api.get('/api/pluginspace/this', {
                headers: {
                    'Authorization': `Bearer ${await getToken()}`
                }
            });
            if(resPluginspace.data.data && resPluginspace.data.data[0] && resPluginspace.data.data[0].hash){
                const resIntegration = await api.post('api/orders/card-payment-get', 
					{
						hash: resPluginspace.data.data[0].hash,
					},
					{
						headers: {
							Authorization: `Bearer ${await getToken()}`,
						},
					}
				);
                
                const newBrandsIntegrations:IntegrationType[] = resIntegration.data.data.data.filter((integration:IntegrationType) => (integration.pluginconfigId === gatewayId && integration.brand !== 'Pix'))
                newBrandsIntegrations.map((brand) => {
                    if(brand.codePay.includes('ECOMMERCE')){
                        brand.codePay = `${brand.codePay}-${brand.brand}`
                    }
                })
                setIntegrationBrands(newBrandsIntegrations);
                setBrand(newBrandsIntegrations[0].codePay)
            }
        } catch (e) {
            console.log(e)
        }
    }

    async function getPlugintemplate(gatewayId: number) {
        await api
			.get(
				`/api/pluginconfig/${gatewayId}`,
				{
					headers: {
						Authorization: `Bearer ${await getToken()}`,
					},
				}
			)
			.then((response) => {
				if(response.data && response.data.data && response.data.data[0] && response.data.data[0].plugintemplateId){
                    setPlugintemplateId(response.data.data[0].plugintemplateId)
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro!',
                        text: 'Não foi possível encontrar forma de pagamento por cartão para esta loja.',
                    }).then(() => navigate(`/${pluginspace_id}/booking`))
                }
			})
			.catch((err) => {
				Swal.fire({
                    icon: 'error',
                    title: 'Erro!',
                    text: 'Não foi possível encontrar forma de pagamento por cartão para esta loja.',
                }).then(() => navigate(`/${pluginspace_id}/booking`))
			});
    }

    function getBooking() {
        const bookingStorage = localStorage.getItem('bookingToPay');
        if(bookingStorage){
            setBooking(JSON.parse(bookingStorage))
        } else {
            Swal.fire({
                icon: 'error',
                title: 'Erro!',
                text: 'Não foi possível encontrar a reserva.',
            }).then(() => navigate(`/${pluginspace_id}/booking`))
        }
    }

    async function getBookingConfig() {
        await api.get('/api/queueReservation/bookingConfig', {
            headers: {
                Authorization: `Bearer ${await getToken()}`,
            },
        }).then((response) => {
            const bookingResponse = response.data;
            if(bookingResponse && bookingResponse.data && bookingResponse.data[0]){
                const bookingConfigItem = bookingResponse.data[0];
                if(bookingConfigItem && bookingConfigItem.able){
                    setBookingConfig(bookingConfigItem)
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Reserva não está habilitada!',
                        text: 'Loja não está com o esquema de reservas habilitada.',
                    }).then(() => navigate(`/${pluginspace_id}`))    
                }
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Reserva não configurada!',
                    text: 'Loja não está com o esquema de reserva configurada.',
                }).then(() => navigate(`/${pluginspace_id}`))
            }
        })
        .catch((err) => {
            console.log(err);
            Swal.fire({
                icon: 'error',
                title: 'Reserva não configurada!',
                text: 'Loja não está com o esquema de reservas configurada.',
            }).then(() => navigate(`/${pluginspace_id}`))
        })
    }

    async function encryptCard() {
        return await api
			.post(
				"/api/cryptography/encryptJson",
				{
					value: {
						amount: bookingConfig?.valuePaymentConfirm,
						cardholderName: name,
						cardNumber: number,
						securityCode: cvv.split("_").join(""),
                        installments: 1,
						expirationMonth: parseInt(cardDate.split("/")[0]),
						expirationYear: parseInt(cardDate.split("/")[1]),
                        gatewayId: config?.content.pdv.gatewayId,
                        plugintemplateId,
                        email: booking?.email,
                        cpf: cpfCnpjNoMask(cpf),
                        phone: telephoneNoMask(booking?.phone ? booking.phone : ''),
                        nameCardBrand: brand,
					},
				},
				{
					headers: {
						Authorization: `Bearer ${await getToken()}`,
					},
				}
			)
			.then((response) => {
				return response.data.data.encryptedData;
			})
			.catch((err) => {
				console.log(err);
				return null;
			});
    }

    async function pagseguroCardData() {
		return await api
			.get("/api/pagseguro/pagseguroPublicKey", {
				headers: {
					Authorization: `Bearer ${await getToken()}`,
				},
			})
			.then(async (response) => {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				const encryptedCard = PagSeguro.encryptCard({
					publicKey: response.data.publicKey,
					holder: name,
					number: number.split(" ").join(""),
					expMonth: parseInt(cardDate.split("/")[0]),
					expYear: parseInt(cardDate.split("/")[1]),
					securityCode: cvv.split("_").join(""),
                    nameCardBrand: brand,
				});
				return encryptedCard;
			})
			.catch((err) => {
				console.log(err);
				return null;
			});
	}

    async function encryptCardPagseguro() {
		const encryptedCard = await pagseguroCardData();
		return await api
			.post(
				"/api/cryptography/encryptJson",
				{
					value: {
						amount: bookingConfig?.valuePaymentConfirm,
						cardNumber: number.split(" ").join(""),
						cardholderName: name,
						securityCode: cvv.split("_").join(""),
						expirationMonth: parseInt(cardDate.split("/")[0]),
						expirationYear: parseInt(cardDate.split("/")[1]),
						encryptedCard: encryptedCard.encryptedCard,
                        gatewayId: config?.content.pdv.gatewayId,
                        plugintemplateId,
                        email: booking?.email,
                        cpf: cpfCnpjNoMask(cpf),
                        phone: telephoneNoMask(booking?.phone ? booking.phone : ''),
                        installments: 1,
					},
				},
				{
					headers: {
						Authorization: `Bearer ${await getToken()}`,
					},
				}
			)
			.then((response) => {
				return response.data.data.encryptedData;
			})
			.catch((err) => {
				console.log(err);
				return null;
			});
	}

    async function setTokenizedCard(response: any) {

		if (response.tokenizedPan) {
			setLoading(true);
			const cardTotvs: CardTotvsType = {
				amount: bookingConfig?.valuePaymentConfirm ? bookingConfig.valuePaymentConfirm : 0,
				deliveryfee: 0,
				brand: response.brand,
				cardholderName: name,
				cardNumber: null,
				expirationMonth: cardDate.split("/")[0],
				expirationYear: cardDate.split("/")[1],
				installments: 1,
				nameCardBrand: response.brandName,
				securityCode: cvv.split("_").join(""),
				tokenizedPan: response.tokenizedPan,
                fingerprintSessionId: response.fingerprintSessionId,
                gatewayId: config?.content.pdv.gatewayId,
                plugintemplateId,
                email: booking?.email,
                cpf: cpfCnpjNoMask(cpf),
                phone: telephoneNoMask(booking?.phone ? booking.phone : ''),
			};
			const encryptedData = await encryptCardTotvs(cardTotvs);
            const subscriptionStorage = localStorage.getItem(window.btoa('subscription_notification'))        
            let subscription = null;
            if(subscriptionStorage && window.atob(subscriptionStorage) && JSON.parse(window.atob(subscriptionStorage))){
                subscription = JSON.parse(window.atob(subscriptionStorage))
            }
			await api.post('/api/queueReservation/booking', {
                date: booking?.date,
                bookingHourId: booking?.bookingHourId,
                bookingEnvironmentId: booking?.bookingEnvironmentId,
                email: booking?.email,
                bookingConfigId: bookingConfig?.id ? bookingConfig.id : 0,
                people: booking?.people,
                name: booking?.name,
                phone: telephoneNoMask(booking?.phone ? booking.phone : ''),
                payment: encryptedData,
                subscription,
                urlNotification: window.location.href.replace("payment", telephoneNoMask(booking?.phone ? booking.phone : ''))
            },
            {
                headers: {
                    Authorization: `Bearer ${await getToken()}`,
                },
            }).then((response) => {
                setLoading(false);
                const bookingResponse = response.data;
                if(bookingResponse.success){
                    Swal.fire({
                        icon: 'success',
                        title: 'Sucesso!',
                        text: 'Você realizou sua reserva.',
                    }).then(() => navigate(`/${pluginspace_id}/booking/success/${telephoneNoMask(booking?.phone ? booking.phone : '')}`))
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro ao realizar reserva!',
                        text: 'Não foi possível realizar reserva.',
                    })    
                }
            })
            .catch((err) => {
                setLoading(false);
                let title = 'Erro ao realizar reserva!';
                let text = 'Não foi possível realizar reserva.';
                if(err && err.response && err.response.data && err.response.data.error){
                    title = err.response.data.error;
                }
                if(err && err.response && err.response.data && err.response.data.message){
                    text = err.response.data.message;
                }
                Swal.fire({
                    icon: 'error',
                    title,
                    text,
                })
            })
		} else {
			setLoading(false);
			Swal.fire({
				icon: 'error',
				title: 'Erro',
				text: 'Não foi possível finalizar sua compra',
			})
		}
	}

    async function encryptCardTotvs(cardTotvs: CardTotvsType) {
		return await api.post('api/cryptography/encryptJson',
			{
				value: cardTotvs
			},
			{
				headers: {
					'Authorization': `Bearer ${await getToken()}`
				}
			}).then((response) => {
				return response.data.data.encryptedData
			})
			.catch((err) => {
				console.log(err);
				return null;
			})
	}

    async function finalizeOrderCardTotvs() {
        await api
        .get(`/api/pluginconfig/${config?.content.pdv.gatewayId}`, {
            headers: {
                Authorization: `Bearer ${await getToken()}`,
            },
        })
        .then(async (response) => {
            const dataPlugin = response.data;
            
            if (dataPlugin.data && dataPlugin.data[0] && dataPlugin.data[0].content) {
                const configTotvs = dataPlugin.data[0].content;
                const tkData = await api
                    .post(`/api/totvshub/accessTotvs`,
                        {
                            gatewayId: config?.content.pdv.gatewayId,
                            pluginspaceId: pluginspaceData?.id ? pluginspaceData?.id : 1
                        },
                        {
                            headers: {
                                Authorization: `Bearer ${await getToken()}`,
                            },
                        })
                if (tkData.data && tkData.data.success && tkData.data.tokenData && tkData.data.tokenData.access_token) {
                    // @ts-ignore
                    clientData = {
                        amount: bookingConfig?.valuePaymentConfirm,
                        currency: "BRL",
                        cardType: "CreditCard",
                        externalPosId: configTotvs.externalposid,
                        externalBusinessUnitId: configTotvs.externalbusinessunitid,
                        externalTransactionId: configTotvs.externaltransactionid,
                        installments: 1,
                        card: {
                            expirationYear: parseInt(cardDate.split("/")[0]),
                            expirationMonth: parseInt(cardDate.split("/")[1]),
                            securityCode: cvv.split("_").join(""),
                            number: number,
                        },
                        getTokenInfoAPI: {
                            // uri: 'https://dev.raas.varejo.totvs.com.br/pay-hub/transacting/card/api/payment-data',
                            uri: configTotvs.urlGetPaymentData,
                            token: tkData.data.tokenData.access_token,
                        }
                    };
                    
                    // @ts-ignore
                    getTokenizedCardPan(clientData, setTokenizedCard);
                } else {
                    setLoading(false)
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro',
                        text: 'Não foi possível finalizar sua compra',
                    })
                    return null
                }
            } else {
                setLoading(false)
                Swal.fire({
                    icon: 'error',
                    title: 'Erro',
                    text: 'Não foi possível finalizar sua compra',
                })
                return null
            }
        })
        .catch((err) => {
            setLoading(false)
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Não foi possível finalizar sua compra',
            })
            return null;
        });
    }

    async function goBooking() {
        
        if(!bookingConfig?.id){
            Swal.fire({
                icon: 'error',
                title: 'Erro!',
                text: `Erro ao encontrar configuração de reserva`,
            })
            return;
        }

        if(!name || !number || number.includes("_") || !cardDate || cardDate.includes("_") || !cvv){
            Swal.fire({
                icon: 'error',
                title: 'Erro!',
                text: `Verifique se preencheu todos os dados corretamente`,
            })
            return;
        }

        if(!config?.content.pdv.gatewayId){
            Swal.fire({
                icon: 'error',
                title: 'Erro!',
                text: `Loja não possui meios de pagamento com cartão.`,
            })
            return;
        }
        
        setLoading(true);
        
        let payment = '';
        if(plugintemplateId === 10){
            payment = await encryptCardPagseguro()
        } else if(plugintemplateId === 25){
            await finalizeOrderCardTotvs()
            return
        } else {
            payment = await encryptCard()
        }
        const subscriptionStorage = localStorage.getItem(window.btoa('subscription_notification'))        
        let subscription = null;
        if(subscriptionStorage && window.atob(subscriptionStorage) && JSON.parse(window.atob(subscriptionStorage))){
            subscription = JSON.parse(window.atob(subscriptionStorage))
        }
        await api.post('/api/queueReservation/booking', {
            date: booking?.date,
            bookingHourId: booking?.bookingHourId,
            bookingEnvironmentId: booking?.bookingEnvironmentId,
            email: booking?.email,
            bookingConfigId: bookingConfig.id,
            people: booking?.people,
            name: booking?.name,
            phone: telephoneNoMask(booking?.phone ? booking.phone : ''),
            payment: payment,
            subscription,
            urlNotification: window.location.href.replace("payment", telephoneNoMask(booking?.phone ? booking.phone : ''))
        },
        {
            headers: {
                Authorization: `Bearer ${await getToken()}`,
            },
        }).then((response) => {
            setLoading(false);
            const bookingResponse = response.data;
            if(bookingResponse.success){
                Swal.fire({
                    icon: 'success',
                    title: 'Sucesso!',
                    text: 'Você realizou sua reserva.',
                }).then(() => navigate(`/${pluginspace_id}/booking/success/${telephoneNoMask(booking?.phone ? booking.phone : '')}`))
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Erro ao realizar reserva!',
                    text: 'Não foi possível realizar reserva.',
                })    
            }
        })
        .catch((err) => {
            setLoading(false);
            let title = 'Erro ao realizar reserva!';
            let text = 'Não foi possível realizar reserva.';
            if(err && err.response && err.response.data && err.response.data.error){
                title = err.response.data.error;
            }
            if(err && err.response && err.response.data && err.response.data.message){
                text = err.response.data.message;
            }
            Swal.fire({
                icon: 'error',
                title,
                text,
            })
        })
        
    }

    async function getConfig(){
        const findConfig = await getAppConfig()
        setConfig(findConfig);
        if(findConfig){
            await loadBrands(findConfig.content.pdv.gatewayId);
            await getPlugintemplate(findConfig.content.pdv.gatewayId);
        }
    }

    return (
        <div className="booking-payment">
            { loading ?
                <div style={{width: '100%', height: 'calc(100vh)'}}>
                    <Loading
                        type="spinningBubbles"
                        color="#FFF"
                        width={64}
                        height={64}
                    />
                </div>
            :
            <div className="content-booking-payment">
                { bookingConfig && (
                    <div className="body-booking-payment">
                        {(bookingConfig.images && bookingConfig.images[1]) && (
                            <div className="booking-image">
                                <img src={bookingConfig.images[1].url} alt="Imagem Reserva 2" />
                            </div>        
                        )}
                        <h2>Só falta um passo para confirmar sua reserva</h2>
                        { booking && (
                            <div className="booking-box">
                                <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
                                <div className="booking-info">
                                    <div className="row">
                                        <b>Data</b>
                                        <p style={{color: '#6E7586'}}>{datetimeToDateBR(booking.date)}</p>
                                        <p style={{color: '#6E7586'}}>{booking.hour}</p>
                                    </div>
                                    <b>{booking.people} {booking.people > 1 ? 'pessoas' : 'pessoa' }</b>
                                    <b>{booking.name}</b>
                                    <div className="row">
                                        <p style={{color: '#6E7586'}}>{booking.phone}</p>
                                        <p style={{color: '#6E7586'}}>{booking.email}</p>
                                    </div>
                                    
                                </div>
                            </div>
                        )}  
                        <p>
                            O restaurante faz uma pré-cobrança de {floatToPrice(centsToFloat(bookingConfig.valuePaymentConfirm))} para confirma a sua reserva. 
                            Esse valor só será cobrado caso você não compareça na data indicada.
                            Se você cancelar a reserva, ou aparecer na data informada, esse valor não será cobrado. 
                        </p>
                        <div className="booking-form">
                            <div className="input">
                                <label>Nome</label>
                                <input 
                                    type="text" 
                                    value={name} 
                                    onChange={(event) => setName(event.target.value)} 
                                />
                            </div>
                            <div className="multiple-input">
                                <div className="input">
                                    <label>CPF</label>
                                    <InputMask
                                        mask={'999.999.999-99'}
                                        type="text" 
                                        value={cpf} 
                                        onChange={(event) => setCpf(event.target.value)}
                                    />
                                </div>
                                <div className="input">
                                <label>Bandeira</label>
                                <select value={brand} onChange={(event) => {
                                    setBrand(event.target.value)
                                }}>
                                    { integrationBrands.map((payment) => (
                                        <option value={payment.brand.toLowerCase()}>{payment.brand.toUpperCase()}</option>
                                    )) }
                                </select>
                            </div>
                            </div>
                            <div className="input">
                                <label>Número do cartão de crédito</label>
                                <InputMask
                                    mask={maskCard}
                                    type="text" 
                                    value={number} 
                                    onChange={(event) => setNumber(event.target.value)}
                                    onFocus={() => {
                                        setMaskCard('9999 9999 9999 9999')
                                    }}
                                    onBlur={() => {
                                        if(number.split("_").join("").split(" ").join("").length == 14) setMaskCard('9999 9999 9999 99')
                                        else if(number.split("_").join("").split(" ").join("").length == 15) setMaskCard('9999 9999 9999 999')
                                        else setMaskCard('9999 9999 9999 9999')
                                    }}
                                />
                            </div>
                            <div className="multiple-input">
                                <div className="input">
                                    <label>Validade</label>
                                    <InputMask
                                        mask={'99/9999'}
                                        type="text" 
                                        value={cardDate}
                                        onChange={(event) => {
                                            setCardDate(event.target.value)
                                        }}
                                    />
                                </div>
                                <div className="input">
                                    <label>Código</label>
                                    <InputMask
                                        mask={maskCvv}
                                        onBeforeInputCapture={(event) => {
                                            if(event.currentTarget.value.split("_").join("").length >= 3) setMaskCvv('9999')
                                            else setMaskCvv('999')
                                        }}
                                        type="text" 
                                        value={cvv}
                                        onChange={(event) => {
                                            if(event.target.value.includes('_')) setMaskCvv('999')
                                            setCvv(event.target.value)
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="button-booking">
                                <MainButton
									color={
										config?.webappcolor.fontbutton
											? config.webappcolor.fontbutton
											: "#636363"
									}
									backgroundColor={
										config?.webappcolor.webappbutton
											? config.webappcolor.webappbutton
											: "#EAEAEA"
									}
									text="Confirmar reserva"
									onClick={() => goBooking()}
								/>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            }
        </div>
    )
}